import React from 'react';

import {useI18n} from '@udemy/i18n';
import {Button} from '@udemy/react-core-components';
import {
    CourseUnit,
    SkillsHubCourseUnit,
    type SkillsHubCourseUnitProps,
} from '@udemy/react-discovery-units';

import {NextTopicsSubUnit, NextTopicsUnitData} from 'src/types/discovery';

import styles from './next-topics-unit.module.less';

/** Items fetched per tab. Matches the monolith's `NextTopicsUnit`. */
const TAB_PAGE_SIZE = 6;

export type NextTopicsUnitProps = Omit<Partial<SkillsHubCourseUnitProps>, 'unit'> & {
    unit: NextTopicsUnitData;
};

/**
 * Renders a `next_topics` recommendation unit as a tabbed skills-hub unit.
 *
 * Discovery sends these units with `available_filters.course_labels`, while `SkillsHubUnit` builds
 * its tabs from `available_filters.units`. The two aren't interchangeable: a course label's `url` is
 * a topic page, whereas a skills-hub subunit's `url` has to be the discovery endpoint the tab
 * fetches from. This maps one onto the other, rewriting `url` to the label-filtered endpoint and
 * keeping the topic page on `topic_url` for the unit CTA.
 *
 * Ported from the monolith's `browse/components/discovery-units/next-topics-unit`. The one
 * deliberate difference is the fallback: the monolith renders an empty `<div>` when there are no
 * labels, which drops the `className` that `DiscoveryUnitRenderer` passes down and so breaks the
 * `.component-margin + .component-margin` spacing rule for the following section. Falling back to
 * `CourseUnit` keeps the spacing and still renders the unit's own items.
 */
export const NextTopicsUnit = ({unit, ...restOfProps}: NextTopicsUnitProps) => {
    const {gettext} = useI18n();

    const courseLabels = unit.available_filters?.course_labels;

    // The casts below are the cost of the `course_labels` type gap: `@udemy/discovery-api` declares
    // `available_filters.units` as required and doesn't know `course_labels` exists, so neither the
    // raw nor the patched unit satisfies the published prop types.
    // TODO: drop once `course_labels` is declared upstream. See src/types/discovery.ts.
    if (!courseLabels?.length) {
        return (
            <CourseUnit {...({...restOfProps, unit} as React.ComponentProps<typeof CourseUnit>)} />
        );
    }

    const patchedUnit = {
        ...unit,
        available_filters: {
            ...unit.available_filters,
            units: courseLabels.map((courseLabel) => ({
                ...courseLabel,
                topic_url: courseLabel.url,
                url: `${unit.url}&f_label_id=${courseLabel.value}&page_size=${TAB_PAGE_SIZE}`,
            })),
        },
    };

    const renderUnitCta = (subUnit: NextTopicsSubUnit) => (
        <Button href={subUnit.topic_url} className={styles['unit-cta']} componentClass="a">
            {gettext('Explore more courses')}
        </Button>
    );

    return (
        <SkillsHubCourseUnit
            {...({
                ...restOfProps,
                showQuickViewBox: restOfProps.showQuickViewBox ?? true,
                unit: patchedUnit,
                renderUnitCta: renderUnitCta as SkillsHubCourseUnitProps['renderUnitCta'],
            } as unknown as React.ComponentProps<typeof SkillsHubCourseUnit>)}
        />
    );
};
